<template>
  <div class="modal-card">
    <CloseIcon :size="24" class="close-modal-icon close-on-click" />

    <div class="modal-card-header">
      <h1 class="title">Export {{ entityTypeLabel }}</h1>
    </div>

    <div class="modal-card-body">
      <div class="form-grid">
        <div class="form-row">
          <label class="form-label">Export Scope</label>
          <SelectInput
            v-model="exportScope"
            :options="scopeOptions"
            placeholder="Select scope..."
            :disabled="isExporting"
          />
        </div>

        <div class="form-row">
          <label class="form-label">Columns to Export</label>
          <SelectInput
            v-model="selectedColumns"
            :options="columnOptions"
            :multiSelect="true"
            placeholder="Select columns..."
            :disabled="isExporting"
          />
        </div>

        <div v-if="isExporting" class="progress-section">
          <div class="progress-info">
            <span class="progress-label">Exporting records...</span>
            <span class="progress-count">{{ exportProgress.current }} / {{ exportProgress.total }}</span>
          </div>
          <div class="progress-bar-container">
            <div class="progress-bar" :style="{ width: progressPercentage + '%' }"></div>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-card-footer">
      <button
        class="button secondary form-button close-on-click"
        :disabled="isExporting"
      >
        Cancel
      </button>
      <button
        class="button primary form-button"
        @click="handleExport"
        :disabled="isExporting || selectedColumns.length === 0 || !exportScope"
      >
        {{ isExporting ? 'Exporting...' : 'Export to CSV' }}
      </button>
    </div>

    <div v-if="error" class="modal-error">{{ error }}</div>
  </div>
</template>

<script>
import SelectInput from '@/components/inputs/SelectInput.vue';
import CloseIcon from 'vue-material-design-icons/Close.vue';

export default {
  name: 'ExportDataModal',
  components: {
    SelectInput,
    CloseIcon
  },
  props: {
    entityType: {
      type: String,
      required: true
    },
    columns: {
      type: Array,
      required: true
    },
    currentPageCount: {
      type: Number,
      required: true
    },
    totalCount: {
      type: Number,
      required: true
    },
    exportConfig: {
      type: Object,
      required: true
    },
    exportProgress: {
      type: Object,
      default: () => ({ current: 0, total: 0 })
    },
    isExporting: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    }
  },
  emits: ['export'],
  data() {
    return {
      exportScope: '',
      selectedColumns: []
    };
  },
  computed: {
    entityTypeLabel() {
      const labels = {
        'patients': 'Patients',
        'tickets': 'Tickets',
        'payments': 'Payments',
        'receivables': 'Receivables',
        'products': 'Products'
      };
      return labels[this.entityType] || this.entityType;
    },
    scopeOptions() {
      return [
        {
          value: 'current',
          label: `Current page (${this.currentPageCount} records)`
        },
        {
          value: 'all',
          label: `All pages (${this.totalCount} records)`
        }
      ];
    },
    columnOptions() {
      return this.columns.map(col => ({
        value: col.key,
        label: this.exportConfig[col.key]?.label || col.label
      }));
    },
    progressPercentage() {
      if (this.exportProgress.total === 0) return 0;
      return Math.round((this.exportProgress.current / this.exportProgress.total) * 100);
    }
  },
  methods: {
    handleExport() {
      this.$emit('export', {
        scope: this.exportScope,
        columns: this.selectedColumns
      });
    }
  }
}
</script>

<style scoped>
.form-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.progress-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.progress-label {
  font-size: 14px;
  color: var(--text-secondary);
}

.progress-count {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.progress-bar-container {
  width: 100%;
  height: 8px;
  background-color: var(--border-color);
  border-radius: var(--border-radius);
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: var(--primary-color);
  transition: width 0.3s ease;
  border-radius: var(--border-radius);
}
</style>
